import forEach from 'lodash/forEach';
import adInterface from '../ads/adInterface';
import adSlotManager from '../ads/adSlotManager';
import audioVideoAdController from '../ads/controllers/audioVideoAdController';
import displayAdController from '../ads/controllers/display/displayAdController';
import fallbackAdsController from '../ads/controllers/fallbackAdsController';
import InstreamAvailsController from '../ads/controllers/instream/InstreamAvailsController';
import truexNetworkController from '../ads/networks/truexNetworkController';
import SlotEvents from '../ads/slotEvents';
import googletagBootstrap from '../bootstrap/googletag';
import logger from '../logging/logger';
import stackAdaptController from '../pixels/stackAdapt';
import googlePal from '../targeting/palSdkController';
import adsSettings from '../utils/adsSettings';
import applicationState, { State, addStateChangeListener, getState, updateState, } from '../utils/applicationState';
import configUtils from '../utils/configUtils';
import { MediumTypes } from '../utils/constants';
import getAdTypeControllerFromSlot from '../utils/getAdTypeControllerFromSlot';
googletagBootstrap.bootstrap();
export default {
    init(settings) {
        applicationState.init(settings);
        displayAdController.init();
        fallbackAdsController.init();
        truexNetworkController.init();
        audioVideoAdController.init();
        googlePal.init();
        adInterface.init();
        this.setCanShowAds();
        this.instreamAvailsController = new InstreamAvailsController();
        this.onProfileChange = this.onProfileChange.bind(this);
        this.onCanShowAdsChange = this.onCanShowAdsChange.bind(this);
        this.updateInstreamParams = this.updateInstreamParams.bind(this);
        addStateChangeListener(State.selectedGuideId, this.onProfileChange);
        addStateChangeListener(State.playerStatus, this.onCanShowAdsChange);
        addStateChangeListener(State.adsEnabled, this.onCanShowAdsChange);
        addStateChangeListener(State.nowPlaying, this.updateInstreamParams);
        stackAdaptController.init();
    },
    setCanShowAds() {
        this.canShowAds = adsSettings.canShowAds();
    },
    handleInstreamAds(adData) {
        logger.debug('mint | received instream ad data', adData);
        this.instreamAvailsController.processAdData(adData);
    },
    async registerSlot(elementId, slotName, dimensions) {
        const screenName = getState(State.currentScreen);
        const matchingSlot = adSlotManager.getSlot(elementId) || {};
        if (!getState(State.isInitialized) ||
            matchingSlot.name === slotName ||
            !configUtils.isValidSlotForScreen(slotName, screenName)) {
            return;
        }
        const slot = adSlotManager.addSlot(slotName, elementId, dimensions);
        logger.info({
            message: 'register.slot.debug.dimensions.issue',
            data: {
                slotNameParam: slotName,
                dimensionsParam: dimensions,
                slot: {
                    name: slot.name,
                    formatDefinition: {
                        dimensions: slot.formatDefinition?.dimensions,
                    },
                },
            },
        });
        if (!slot) {
            return;
        }
        const { adTypeController } = slot;
        if (adTypeController && !adTypeController.isRegistered(slot)) {
            await adTypeController.registerSlot(slot);
        }
        fallbackAdsController.registerSlot(slot);
    },
    deregisterSlot(elementIdOrSlotName) {
        if (!getState(State.isInitialized)) {
            return;
        }
        const slot = adSlotManager.getSlot(elementIdOrSlotName);
        if (slot?.formatDefinition) {
            const adTypeController = getAdTypeControllerFromSlot(slot);
            if (adTypeController?.isRegistered(slot)) {
                adTypeController.releaseSlot(slot);
                adTypeController.deregisterSlot(slot);
            }
            fallbackAdsController.releaseSlot(slot);
            fallbackAdsController.deregisterSlot(slot);
        }
        adSlotManager.removeSlot(elementIdOrSlotName);
    },
    clearSlot(elementIdOrSlotName, shouldIgnoreAdVisibilityEventOnce) {
        if (!getState(State.isInitialized)) {
            return;
        }
        const slot = adSlotManager.getSlot(elementIdOrSlotName);
        if (slot?.formatDefinition) {
            const adTypeController = getAdTypeControllerFromSlot(slot);
            if (adTypeController?.isRegistered(slot) && adTypeController.clearSlot) {
                adTypeController.clearSlot(slot, shouldIgnoreAdVisibilityEventOnce);
            }
        }
    },
    async requestSlot(elementIdOrSlotName) {
        if (!(getState(State.isInitialized) && getState(State.adsEnabled))) {
            return;
        }
        const slot = adSlotManager.getSlot(elementIdOrSlotName);
        if (!slot) {
            return;
        }
        const slotName = slot.name;
        const screenName = getState(State.currentScreen);
        const { formatDefinition, adTypeController } = slot;
        if (!(formatDefinition &&
            adTypeController &&
            configUtils.isValidSlotForScreen(slotName, screenName))) {
            return;
        }
        if (slot.adTypeController) {
            slot.adTypeController.releaseSlot(slot);
        }
        const refreshSlot = () => this.requestSlot(elementIdOrSlotName);
        slot.off(SlotEvents.refresh);
        slot.once(SlotEvents.refresh, refreshSlot);
        if (!adTypeController.isRegistered(slot)) {
            adTypeController.registerSlot(slot);
        }
        await adTypeController.serveAd({ slot, adTypeController });
    },
    requestPremiumUnlockActivity(params) {
        const { slotName, elementId } = params;
        let slot = adSlotManager.getSlot(elementId);
        if (!slot) {
            this.registerSlot(elementId, slotName);
            slot = adSlotManager.getSlot(elementId);
        }
        params.slot = slot;
        truexNetworkController.serveAd(params);
    },
    updateState(stateName, value) {
        updateState(stateName, value);
    },
    updateUnlockDetails(value) {
        applicationState.updateUnlockDetails(value);
    },
    getState(stateName) {
        return getState(stateName);
    },
    requestAllDisplaySlots() {
        if (adsSettings.canShowAds() && !this.canShowAds) {
            forEach(adSlotManager.getSlots(), (slot) => {
                if (slot.medium === MediumTypes.display) {
                    this.requestSlot(slot.elementId);
                }
            });
        }
    },
    onCanShowAdsChange() {
        this.requestAllDisplaySlots();
        this.setCanShowAds();
    },
    updateLotameAudiences(audiences) {
        updateState(State.lotameAudiences, audiences);
        this.instreamAvailsController.updateInstreamParams();
        googletag.cmd.push(() => {
            googletag.pubads().setTargeting('lotame', audiences);
        });
    },
    updateInstreamParams() {
        this.instreamAvailsController.updateInstreamParams();
    },
    onProfileChange(selectedGuideId) {
        if (selectedGuideId) {
            this.requestAllDisplaySlots();
        }
        this.setCanShowAds();
        this.instreamAvailsController.updateInstreamParams();
    },
    disableAds() {
        updateState(State.adsEnabled, false);
    },
    async preload() {
        await audioVideoAdController.preload();
    },
    play() {
        audioVideoAdController.play();
    },
    pause() {
        audioVideoAdController.pause();
    },
    stop() {
        audioVideoAdController.stop();
    },
    volume(volume) {
        audioVideoAdController.volume(volume);
    },
    resize(width, height) {
        audioVideoAdController.resize(width, height);
    },
    getPalNonce() {
        return googlePal.getNonce();
    },
};
